/**
 * One work order: status, vendor assignment, notes, and photos.
 *
 * Assigning a vendor emails them — see the service. This page just shows the
 * result; it doesn't need to know that happened, the same way LeaseShow
 * doesn't need to know recordDepositReceived wrote a Notice row.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useInvalidate, useList, useOne } from '@refinedev/core';
import { WORK_ORDER_STATUSES } from 'shared';
import { api } from '../../../lib/api.js';
import { humanizeEnum } from '../../../components/Form.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { useCan } from '../../../lib/capabilities.js';

interface WorkOrderNote {
  id: string;
  note: string;
  createdAt: string | null;
}

interface WorkOrderRecord {
  id: string;
  unitLabel: string;
  propertyName: string;
  vendorId: string | null;
  vendorName: string | null;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  cost: string | null;
  scheduledOn: string | null;
  completedAt: string | null;
  notes: WorkOrderNote[];
}

interface VendorOption {
  id: string;
  name: string;
  trade: string | null;
  archivedAt: string | null;
}

export function WorkOrderShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const canWrite = useCan('maintenance:write');
  const canWriteCost = useCan('maintenance:write-cost');
  const canWriteDocuments = useCan('documents:write');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [costInput, setCostInput] = useState('');

  const { data, isLoading } = useOne<WorkOrderRecord>({
    resource: 'work-orders',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: vendorsData } = useList<VendorOption>({
    resource: 'vendors',
    pagination: { current: 1, pageSize: 200 },
  });

  const workOrder = data?.data;

  useEffect(() => {
    setCostInput(workOrder?.cost ?? '');
  }, [workOrder?.cost]);

  async function refresh() {
    await invalidate({
      resource: 'work-orders',
      invalidates: ['detail', 'list'],
      ...(id ? { id } : {}),
    });
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That action failed.');
    } finally {
      setBusy(false);
    }
  }

  function changeStatus(status: string) {
    if (status === workOrder?.status) return;
    void run(() =>
      api(`/work-orders/${id ?? ''}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    );
  }

  function assignVendor(vendorId: string) {
    void run(() =>
      api(`/work-orders/${id ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify({ vendorId: vendorId || null }),
      }),
    );
  }

  function saveCost() {
    void run(() =>
      api(`/work-orders/${id ?? ''}/cost`, {
        method: 'PATCH',
        body: JSON.stringify({ cost: costInput.trim() === '' ? null : costInput.trim() }),
      }),
    );
  }

  function addNote() {
    if (!noteText.trim()) return;
    void run(async () => {
      await api(`/work-orders/${id ?? ''}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note: noteText }),
      });
      setNoteText('');
    });
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!workOrder) return <p className="alert alert--error">Work order not found.</p>;

  const activeVendors = (vendorsData?.data ?? []).filter((v) => !v.archivedAt);

  return (
    <section className="stack">
      <div className="row">
        <h1>{workOrder.title}</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/work-orders')}
        >
          ← All work orders
        </button>
      </div>

      {error ? <p className="alert alert--error">{error}</p> : null}

      <div className="card stack">
        <div className="row">
          <span className={workOrder.priority === 'EMERGENCY' ? 'pill pill--late' : 'pill'}>
            {humanizeEnum(workOrder.priority)}
          </span>
          <span className="pill">{humanizeEnum(workOrder.status)}</span>
          <span className="muted">
            {workOrder.unitLabel}, {workOrder.propertyName}
          </span>
        </div>
        {workOrder.description ? <p>{workOrder.description}</p> : null}
        <p className="muted">
          {workOrder.scheduledOn ? `Scheduled ${workOrder.scheduledOn}` : 'Not scheduled'}
          {workOrder.completedAt ? ` · completed ${workOrder.completedAt.slice(0, 10)}` : ''}
        </p>

        <p className="muted">Cost: {workOrder.cost ? `$${workOrder.cost}` : 'Not yet recorded'}</p>

        {canWriteCost ? (
          <div className="row">
            <input
              className="field__input"
              type="text"
              inputMode="decimal"
              placeholder="e.g. 450.00"
              value={costInput}
              disabled={busy}
              onChange={(event) => setCostInput(event.target.value)}
            />
            <button type="button" className="button" disabled={busy} onClick={saveCost}>
              Save cost
            </button>
          </div>
        ) : null}

        {canWrite ? (
          <div className="row">
            <select
              className="field__input"
              value={workOrder.status}
              disabled={busy}
              onChange={(event) => changeStatus(event.target.value)}
            >
              {WORK_ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {humanizeEnum(s)}
                </option>
              ))}
            </select>

            <select
              className="field__input"
              value={workOrder.vendorId ?? ''}
              disabled={busy}
              onChange={(event) => assignVendor(event.target.value)}
            >
              <option value="">Unassigned</option>
              {activeVendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                  {vendor.trade ? ` (${vendor.trade})` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <h2>Notes</h2>
      {workOrder.notes.length === 0 ? (
        <p className="muted">No notes yet.</p>
      ) : (
        <ul className="stack">
          {workOrder.notes.map((note) => (
            <li key={note.id} className="card">
              <p>{note.note}</p>
              <p className="muted">{note.createdAt?.slice(0, 16).replace('T', ' ')}</p>
            </li>
          ))}
        </ul>
      )}
      {canWrite ? (
        <div className="row">
          <input
            className="field__input"
            placeholder="Add a note — vendor called back, coming Tuesday…"
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={busy || !noteText.trim()}
            onClick={addNote}
          >
            Add note
          </button>
        </div>
      ) : null}

      <h2>Files</h2>
      <DocumentList
        filter={{ workOrderId: workOrder.id }}
        canDelete={canWriteDocuments}
        emptyMessage="No photos or documents yet."
      />
      {canWriteDocuments ? (
        <details>
          <summary>Upload a photo</summary>
          <div className="card">
            <PhotoUpload
              parent={{ workOrderId: workOrder.id }}
              defaultDocumentType="INSPECTION_PHOTO"
              onUploaded={() => {
                void invalidate({ resource: 'documents', invalidates: ['list'] });
              }}
            />
          </div>
        </details>
      ) : null}
    </section>
  );
}
